// Lifetime Warranty Authority Cluster — spoke content that supports the
// /lifetime-warranty pillar. Data-driven so new spokes need no new code.
// Marketing rule: always "Lifetime Warranty" — never "Limited Lifetime Warranty" in headlines.

export const WARRANTY_SPOKES = [
  {
    slug: "what-qualifies",
    navLabel: "What Qualifies",
    eyebrow: "Qualifying Equipment",
    h1: "Which HVAC Systems Qualify for the Lifetime Warranty?",
    subhead: "Split systems, gas furnaces, package units, and mini splits — installed by Aquila Air & Heating.",
    intro:
      "Lifetime Warranty protection from Aquila Air & Heating is available on qualifying HVAC systems professionally installed by our team across Brevard County. The exact coverage depends on the equipment you choose, proper installation, warranty registration, and staying current on the required maintenance. Here is how qualification works and which systems are eligible.",
    seo: {
      title: "Which HVAC Systems Qualify for the Lifetime Warranty? | Aquila Air & Heating",
      description:
        "See which HVAC systems qualify for the Aquila Air & Heating Lifetime Warranty — split systems, gas furnaces, package units, and mini splits installed in Brevard County, FL.",
    },
    sections: [
      {
        h2: "Equipment Eligible for Lifetime Warranty Protection",
        paragraphs: [
          "When Aquila Air & Heating installs your new comfort system, qualifying equipment may be eligible for our Lifetime Warranty protection. Eligibility is confirmed for your specific system before your installation is complete, so you know exactly what is covered.",
        ],
        bullets: [
          "Split Systems — central air conditioner and air handler combinations for whole-home cooling and heating.",
          "Gas Furnaces — high-efficiency gas heating for dependable warmth on cool Space Coast nights.",
          "Package Units — all-in-one heating and cooling systems common on many Florida homes.",
          "Mini Splits — ductless systems for room-by-room comfort, additions, garages, and Florida rooms.",
        ],
      },
      {
        h2: "What Determines Whether Your System Qualifies",
        paragraphs: [
          "Qualification is not automatic on every unit. Availability depends on the specific equipment selected, professional installation to manufacturer and applicable code requirements, warranty registration, and ongoing maintenance compliance.",
          "Our Comfort Advisors walk you through the qualifying options during your free consultation and confirm the Lifetime Warranty protection available for the exact system you choose — before any paperwork is signed.",
        ],
        bullets: [
          "The equipment type and model you select.",
          "Professional installation and system commissioning by Aquila Air & Heating.",
          "Timely warranty registration and retained documentation.",
          "Enrollment in the required maintenance plan with twice-yearly service.",
        ],
      },
      {
        h2: "Why Professional Installation Matters",
        paragraphs: [
          "Protection begins long before a warranty is ever needed. Proper equipment sizing, careful installation, correct airflow and refrigerant charge, and complete commissioning all contribute to how long your system lasts in Florida's demanding heat and humidity. That is why Lifetime Warranty protection is tied to a professionally installed system.",
        ],
      },
    ],
    faqs: [
      { q: "Which HVAC systems qualify for the Lifetime Warranty?", a: "Qualifying Split Systems, Gas Furnaces, Package Units, and Mini Splits installed by Aquila Air & Heating may be eligible for Lifetime Warranty protection. Our team confirms the coverage available for your specific system before installation is complete." },
      { q: "Is every new system automatically covered?", a: "No. Availability depends on the specific equipment, professional installation, warranty registration, and maintenance compliance. We review exactly what applies to your system up front." },
      { q: "How do I know if the system I want qualifies?", a: "During your free consultation, our Comfort Advisors confirm the Lifetime Warranty protection available for the equipment you are considering, along with any registration or maintenance requirements." },
      { q: "Does the Lifetime Warranty apply to systems installed by another company?", a: "Lifetime Warranty protection is available on qualifying equipment professionally installed by Aquila Air & Heating. Ask our team about your options when you are ready to replace your system." },
    ],
    relatedServices: [
      { to: "/ac-installation", label: "AC Installation" },
      { to: "/heat-pumps", label: "Heat Pumps & Mini Splits" },
      { to: "/heating-installation", label: "Heating Installation" },
    ],
  },
  {
    slug: "vs-manufacturer-warranty",
    navLabel: "vs. Manufacturer Warranty",
    eyebrow: "Coverage Compared",
    h1: "Lifetime Warranty vs. Standard Manufacturer Warranty",
    subhead: "How our Lifetime Warranty protection compares to the typical 10-year manufacturer parts warranty.",
    intro:
      "Most new HVAC systems come with a standard manufacturer parts warranty — often around 10 years when registered. The Aquila Air & Heating Lifetime Warranty is additional protection available to qualifying customers, designed to give Brevard County homeowners more confidence in their investment. Here is how the two compare and work together.",
    seo: {
      title: "Lifetime Warranty vs. Manufacturer Warranty | Aquila Air & Heating",
      description:
        "Understand the difference between a standard HVAC manufacturer warranty and the Aquila Air & Heating Lifetime Warranty available on qualifying systems in Brevard County, FL.",
    },
    sections: [
      {
        h2: "The Standard Manufacturer Warranty",
        paragraphs: [
          "When you buy a new HVAC system, the manufacturer typically provides a parts warranty — commonly up to 10 years on major components when the system is registered on time. This covers defects in covered parts but usually does not include labor, and it has its own terms, conditions, and exclusions set by the manufacturer.",
        ],
      },
      {
        h2: "The Aquila Lifetime Warranty",
        paragraphs: [
          "The Aquila Air & Heating Lifetime Warranty is additional protection available to qualifying customers who purchase and install a new HVAC system through us. It is designed to extend confidence and peace of mind beyond the standard manufacturer coverage on qualifying equipment.",
          "Because it is available on qualifying systems only, coverage, eligibility, terms, and requirements are confirmed for your specific system before installation is complete. Staying enrolled in the required maintenance plan keeps qualifying coverage in force.",
        ],
      },
      {
        h2: "How They Work Together",
        paragraphs: [
          "Think of it as layered protection: the manufacturer warranty covers eligible parts on their terms, while the Aquila Lifetime Warranty adds another layer of confidence for qualifying homeowners. Proper installation, registration, and twice-yearly maintenance keep both working the way they should.",
        ],
        bullets: [
          "Manufacturer warranty: covered parts, typically ~10 years when registered, manufacturer's terms.",
          "Aquila Lifetime Warranty: additional protection on qualifying systems, tied to maintenance compliance.",
          "Both benefit from professional installation, timely registration, and regular service.",
        ],
      },
    ],
    faqs: [
      { q: "How is the Lifetime Warranty different from the manufacturer warranty?", a: "The manufacturer warranty covers eligible parts for a set period (often ~10 years when registered). The Aquila Lifetime Warranty is additional protection available to qualifying customers on qualifying systems, designed to extend confidence beyond the standard coverage." },
      { q: "Does the Lifetime Warranty replace the manufacturer warranty?", a: "No. It is additional protection that works alongside your manufacturer coverage. Each has its own terms, conditions, and requirements." },
      { q: "Is labor covered?", a: "Coverage details, including anything related to labor, depend on the specific program and equipment. Our team reviews exactly what applies to your system before installation." },
      { q: "What keeps my Lifetime Warranty in force?", a: "Qualifying coverage generally requires professional installation, warranty registration, and staying enrolled in the required maintenance plan with twice-yearly service. We explain the exact requirements for your system." },
    ],
    relatedServices: [
      { to: "/ac-installation", label: "AC Installation" },
      { to: "/financing", label: "Financing Options" },
      { to: "/maintenance-plan", label: "Maintenance Membership" },
    ],
  },
  {
    slug: "maintenance-requirements",
    navLabel: "Maintenance Requirements",
    eyebrow: "Keeping Coverage Active",
    h1: "How to Keep Your Lifetime Warranty Active",
    subhead: "Twice-yearly professional maintenance is the key to keeping qualifying coverage in force.",
    intro:
      "A qualifying Lifetime Warranty stays in force when your system remains enrolled in the required Aquila maintenance plan and receives professional HVAC service twice per year — roughly every six months. This regular maintenance protects your equipment, preserves efficiency in Florida's heat and humidity, and keeps your coverage active. Here is what that looks like.",
    seo: {
      title: "How to Keep Your Lifetime Warranty Active | Aquila Air & Heating",
      description:
        "Learn the maintenance requirements that keep the Aquila Air & Heating Lifetime Warranty active — twice-yearly professional HVAC service for Brevard County homeowners.",
    },
    sections: [
      {
        h2: "The Twice-Yearly Maintenance Requirement",
        paragraphs: [
          "To keep qualifying Lifetime Warranty coverage in force, your system must stay enrolled in the required Aquila maintenance plan and receive professional service twice per year, approximately every six months, subject to the applicable warranty terms and conditions.",
          "In Florida, that cadence usually means a spring cooling tune-up before the long summer and a fall heating check before the cooler months — the two seasons that put the most demand on your equipment.",
        ],
      },
      {
        h2: "What a Maintenance Visit Includes",
        paragraphs: [
          "Each visit is a thorough, documented check-up that catches small issues before they become breakdowns and keeps your system running efficiently.",
        ],
        bullets: [
          "Refrigerant charge and airflow verification.",
          "Coil, drain line, and condensate inspection and cleaning.",
          "Electrical connections, capacitors, and safety controls checked.",
          "Filter guidance and thermostat calibration.",
          "Documented service history to support your warranty.",
        ],
      },
      {
        h2: "We Track It So You Don't Have To",
        paragraphs: [
          "When you enroll, Aquila Air & Heating schedules and tracks your twice-yearly visits and sends reminders as each service comes due. Our team keeps a documented service record for your system, so staying compliant is effortless and your qualifying coverage stays protected.",
        ],
      },
    ],
    faqs: [
      { q: "How often does my system need maintenance to keep the Lifetime Warranty?", a: "Qualifying coverage generally requires professional HVAC service twice per year — approximately every six months — while staying enrolled in the required Aquila maintenance plan, subject to the applicable warranty terms." },
      { q: "What happens if I miss a maintenance visit?", a: "Missing required maintenance can affect your qualifying coverage. We send reminders and track your visits to help you stay current; contact us right away if you fall behind so we can help." },
      { q: "Do I have to use Aquila Air & Heating for the maintenance?", a: "Qualifying Lifetime Warranty coverage is tied to enrollment in the required Aquila maintenance plan. Our team performs and documents the service so your coverage requirements are met." },
      { q: "Does maintenance really matter in Florida?", a: "Yes. Florida systems run harder and longer than almost anywhere in the country. Twice-yearly maintenance preserves efficiency, protects your equipment, and keeps your qualifying warranty in force." },
    ],
    relatedServices: [
      { to: "/maintenance-plan", label: "Maintenance Membership" },
      { to: "/ac-maintenance", label: "AC Maintenance" },
      { to: "/service-agreement", label: "Comfort Club Plans" },
    ],
  },
  {
    slug: "brevard-county",
    navLabel: "Brevard County",
    eyebrow: "Local Coverage",
    h1: "Lifetime HVAC Warranty in Brevard County, FL",
    subhead: "Florida's first HVAC company offering a Lifetime Warranty — serving the Space Coast from Mims to Palm Bay.",
    intro:
      "Aquila Air & Heating is proud to offer qualifying Brevard County homeowners a Lifetime Warranty on their new HVAC systems. From Titusville and Cocoa to Melbourne and Palm Bay, our Space Coast neighbors run their air conditioners harder than almost anyone — which is exactly why long-term protection matters so much here.",
    seo: {
      title: "Lifetime HVAC Warranty in Brevard County, FL | Aquila Air & Heating",
      description:
        "Qualifying Brevard County homeowners can protect a new HVAC system with the Aquila Air & Heating Lifetime Warranty. Serving Cocoa, Titusville, Melbourne, Palm Bay & the Space Coast.",
    },
    sections: [
      {
        h2: "Why a Lifetime Warranty Matters on the Space Coast",
        paragraphs: [
          "Brevard County's heat, humidity, salt air near the coast, and long cooling season put unusual stress on HVAC equipment. Systems here often run ten to twelve months a year. That workload makes the confidence of a Lifetime Warranty especially valuable — and makes the twice-yearly maintenance that keeps it active especially important.",
        ],
      },
      {
        h2: "Serving Homeowners Across Brevard County",
        paragraphs: [
          "We install and protect qualifying HVAC systems throughout the Space Coast, and our local technicians know the specific comfort challenges of coastal and inland Brevard homes alike.",
        ],
        bullets: [
          "Titusville, Mims & North Brevard",
          "Cocoa, Rockledge & Merritt Island",
          "Viera, Suntree & Melbourne",
          "Palm Bay, West Melbourne & the South County",
        ],
      },
      {
        h2: "Florida's First HVAC Company Offering a Lifetime Warranty",
        paragraphs: [
          "Aquila Air & Heating built its reputation on straight talk, solid work, and lasting comfort. Offering a Lifetime Warranty on qualifying systems is our way of standing behind the work long after the installation is done. When you are ready to replace your system, ask our team about the qualifying equipment and the Lifetime Warranty protection available for your Brevard County home.",
        ],
      },
    ],
    faqs: [
      { q: "Do you offer the Lifetime Warranty across all of Brevard County?", a: "Yes. Aquila Air & Heating serves the entire Space Coast — from Mims and Titusville through Cocoa, Melbourne, and Palm Bay — and offers Lifetime Warranty protection on qualifying systems we install." },
      { q: "Why does a Lifetime Warranty matter more in Florida?", a: "Brevard County systems run nearly year-round in intense heat and humidity, which puts more stress on equipment. Long-term protection plus twice-yearly maintenance helps protect your comfort and your investment." },
      { q: "Is Aquila really Florida's first HVAC company offering a Lifetime Warranty?", a: "Aquila Air & Heating proudly positions itself as Florida's first HVAC company offering a Lifetime Warranty on qualifying systems. Coverage, eligibility, and terms are confirmed for your specific system." },
      { q: "How do I get started in my Brevard County home?", a: "Request a free consultation. Our Comfort Advisors review qualifying equipment and the Lifetime Warranty protection available for your home before you make any decision." },
    ],
    relatedServices: [
      { to: "/service-areas", label: "Service Areas" },
      { to: "/ac-installation", label: "AC Installation" },
      { to: "/ac-repair", label: "AC Repair" },
    ],
  },
];

export const getSpoke = (slug) => WARRANTY_SPOKES.find((s) => s.slug === slug);
